import "./cloud-migration.css";
import { useRef, useState } from "react";
import { useTranslation } from "./i18n";
import { type CloudImportPreview, MAX_CLOUD_IMPORT_BYTES } from "./lib/localStore/cloudImport";
import type { Collection } from "./lib/localStore/schema";

export type CloudImportActions = {
  onPreviewCloudImport: (text: string) => Promise<CloudImportPreview>;
  onImportCloudExport: (text: string, checksum: string, targetChecksum: string) => Promise<void>;
};

export default function CloudImportPanel({
  onPreviewCloudImport,
  onImportCloudExport,
  disabled,
}: CloudImportActions & { disabled: boolean }) {
  const { t } = useTranslation("cloudMigration");
  const [file, setFile] = useState<File>();
  const contents = useRef("");
  const [preview, setPreview] = useState<CloudImportPreview>();
  const [confirmed, setConfirmed] = useState(false);
  const [state, setState] = useState<"idle" | "checking" | "ready" | "importing" | "imported" | "error">("idle");
  const [error, setError] = useState("");
  const busy = state === "checking" || state === "importing";

  async function checkFile() {
    if (!file || busy || disabled) return;
    setState("checking");
    setError("");
    setPreview(undefined);
    setConfirmed(false);
    contents.current = "";
    try {
      if (file.size > MAX_CLOUD_IMPORT_BYTES) throw new Error(t("tooLarge"));
      const text = await file.text();
      const checked = await onPreviewCloudImport(text);
      contents.current = text;
      setPreview(checked);
      setState("ready");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("failed"));
      setState("error");
    }
  }

  async function importFile() {
    if (!preview || !confirmed || busy || disabled) return;
    if (!window.confirm(t("confirm"))) return;
    setState("importing");
    setError("");
    try {
      await onImportCloudExport(contents.current, preview.checksum, preview.targetChecksum);
      contents.current = "";
      setConfirmed(false);
      setFile(undefined);
      setState("imported");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("failed"));
      setState("error");
    }
  }

  return (
    <section
      className="paper-card cloud-migration-panel"
      aria-label={t("title")}
      aria-busy={busy}
      data-import-state={state}
    >
      <h2>{t("title")}</h2>
      <label>
        {t("file")}
        <input
          type="file"
          accept=".json,application/json"
          disabled={busy || disabled}
          onChange={(event) => {
            setFile(event.target.files?.[0]);
            setPreview(undefined);
            setConfirmed(false);
            setError("");
            setState("idle");
            contents.current = "";
          }}
        />
      </label>
      <button type="button" disabled={!file || busy || disabled} onClick={() => void checkFile()}>
        {t(busy ? "working" : "check")}
      </button>
      {preview && (
        <section className="cloud-import-review" aria-label={t("previewTitle")}>
          <h3>{t("previewTitle")}</h3>
          <dl>
            <div>
              <dt>{t("source")}</dt>
              <dd>{preview.sourceWorkspaceId}</dd>
            </div>
            <div>
              <dt>{t("target")}</dt>
              <dd>{preview.targetWorkspaceId}</dd>
            </div>
            <div>
              <dt>{t("exportedAt")}</dt>
              <dd>{preview.exportedAt}</dd>
            </div>
            <div>
              <dt>{t("checksum")}</dt>
              <dd style={{ overflowWrap: "anywhere" }}>
                <code>{preview.checksum}</code>
              </dd>
            </div>
          </dl>
          <table className="cloud-import-counts">
            <caption>{t("counts")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("collection")}</th>
                <th scope="col">{t("current")}</th>
                <th scope="col">{t("incoming")}</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(preview.recordCounts) as Collection[])
                .filter((key) => preview.recordCounts[key] || preview.currentRecordCounts[key])
                .map((key) => (
                  <tr key={key}>
                    <th scope="row">{t(`collections.${key}`)}</th>
                    <td>{preview.currentRecordCounts[key]}</td>
                    <td>{preview.recordCounts[key]}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <p>{t("replaceNotice")}</p>
          {state !== "imported" && (
            <>
              <label>
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={busy || disabled}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                {t("acknowledge")}
              </label>
              <button type="button" disabled={!confirmed || busy || disabled} onClick={() => void importFile()}>
                {t("import")}
              </button>
            </>
          )}
        </section>
      )}
      {busy && <p role="status">{t("working")}</p>}
      {state === "imported" && <p role="status">{t("success")}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
